<template>
  <div>
    <div class="row p-5">
      <div class="col-10">
        <div v-if="channelAlertShow">
          <alert :type="channelAlertType" :content="channelAlertText"/>
        </div>
        <errors-alert :errors="errors" :omit-fields="['viber_business_messages.default_message_ttl']"/>
        <form @submit.prevent="promiseUpdateChannel = updateChannel()">
          <div class="container-fluid">

            <channel-detail-field v-if="!editMode">
              <div slot="label">Channel Name:</div>
              <div slot="value">{{channel.name}}</div>
            </channel-detail-field>
            <div v-else="" class="row form-group mb-0">
              <label for="channel-name" class="col-form-label col-4 col-xl-3 text-muted">Channel Name:</label>
              <div class="col-8 col-xl-9">
                <input type="text"
                       id="channel-name"
                       class="form-control mb-0"
                       :class="{'is-invalid': showChannelNameInvalidMessage}"
                       v-model="channelData.name">
                <div v-show="showChannelNameInvalidMessage" class="invalid-feedback">
                  Please provide a Channel Name.
                </div>
              </div>
            </div>

            <channel-detail-field class="mt-3">
              <div slot="label">Channel ID:</div>
              <div slot="value">{{ channel.id }}</div>
            </channel-detail-field>

            <channel-detail-field v-if="!editMode && showMode()"
                                  class="mt-3">
              <div slot="label">Mode:
                <span id="mode_info" v-b-popover:mode_info.html.hover.righttop="popoverTextMode">
                  <icon name="info-circle"/>
                </span>
              </div>
              <div slot="value" :class="modeClass">{{ modeLabel }}</div>
            </channel-detail-field>
            <div v-else-if="showMode()"
                 class="row form-group mt-3 mb-0">
              <label for="mode" class="col-form-label col-4 col-xl-3 text-muted">
                Mode:
                <span id="mode_info_edit" v-b-popover:mode_info_edit.html.hover.righttop="popoverTextMode">
                  <icon name="info-circle"/>
                </span>
              </label>
              <div class="col-8 col-xl-9">
                <form-select id="mode"
                             :value="channel.mode"
                             elementClasses="row form-group mb-0"
                             inputClasses="col-3 mb-0"
                             @form-select="channel.mode = $event">
                  <option slot="options" v-for="(value, key) in modes" :value="key" :key="key">
                    {{ value }}
                  </option>
                </form-select>
              </div>
            </div>
            <!-- platform specific fields -->
            <!-- TODO 'amio_chat' as a global constant -->
            <facebook-info v-if="channel.type === 'facebook_messenger'"
                           class="mt-3"
                           v-on:channel-alert="handleChannelAlert"
                           :edit-mode="editMode"/>

            <mobile-info v-if="channel.type === 'mobile'"
                         class="mt-3"
                         v-on:update-mode-view="updateModeView"
                         :edit-mode="editMode"/>

            <rbm-info v-if="channel.type === 'rbm'"
                      class="mt-3"
                      v-on:channel-alert="handleChannelAlert"
                      v-on:update-mode-view="updateModeView"
                      :edit-mode="editMode"/>

            <telegram-info v-if="channel.type === 'telegram'"
                           class="mt-3"
                           v-on:channel-alert="handleChannelAlert"
                           :edit-mode="editMode"/>

            <viber-info v-if="channel.type === 'viber'"
                        class="mt-3"
                        v-on:channel-alert="handleChannelAlert"/>

            <viber-business-messages-info v-if="channel.type === 'viber_business_messages'"
                                          class="mt-3"
                                          :edit-mode="editMode"
                                          :errors="errors"
                                          v-on:update-mode-view="updateModeView"
                                          v-on:channel-alert="handleChannelAlert"/>

            <whatsapp-info v-if="channel.type === 'whatsapp'"
                         class="mt-3"
                         v-on:update-mode-view="updateModeView"
                         :edit-mode="editMode"/>

            <div v-if="editMode" class="row mt-3">
              <div class="col-12">
                <button class="btn btn-success"
                        v-busy-button="promiseUpdateChannel">
                  Save
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="col-2">
        <toggle-edit-button class="d-flex justify-content-end"
                            :editMode="editMode"
                            @click="toggleEditMode"/>
      </div>
    </div>
  </div>
</template>

<script>
  import FacebookInfo from '@/components/administration/channel/facebook/FacebookInfo'
  import ViberInfo from '@/components/administration/channel/viber/ViberInfo'
  import RbmInfo from '@/components/administration/channel/rbm/RbmInfo'
  import TelegramInfo from '@/components/administration/channel/telegram/TelegramInfo'
  import ViberBusinessMessagesInfo from '@/components/administration/channel/viber/ViberBusinessMessagesInfo'
  import WhatsappInfo from '@/components/administration/channel/whatsapp/WhatsappInfo'
  import ToggleEditButton from '@/components/administration/common/ToggleEditButton'
  import ChannelDetailField from '@/components/administration/common/Field'
  import ErrorsAlert from '@/components/administration/common/ErrorsAlert'
  import MobileInfo from '@/components/administration/channel/mobile/MobileInfo'
  import eventBus from '@/services/event-bus/event-bus'
  import channelService from '@/services/amio-api/channel.service'
  import FormSelect from '@/components/administration/common/form/FormSelect'
  import Alert from '@/components/administration/common/Alert'
  import clone from 'ramda/src/clone'
  import merge from 'ramda/src/merge'
  import Vue from 'vue'

  export default {
    components: {
      ChannelDetailField,
      FormSelect,
      ErrorsAlert,
      FacebookInfo,
      MobileInfo,
      RbmInfo,
      TelegramInfo,
      WhatsappInfo,
      ToggleEditButton,
      ViberInfo,
      ViberBusinessMessagesInfo,
      Alert
    },

    store: {
      channel: 'administration.channelDetail.channel',
      channelSpecificData: 'administration.channelDetail.channelEdit.editableData'
    },

    data() {
      return {
        promiseUpdateChannel: null,
        showChannelNameInvalidMessage: false,
        editMode: false,
        errors: [],
        channelAlertShow: false,
        channelAlertText: '',
        channelAlertType: '',
        modes: {},
        modeClass: '',
        popoverTextMode: ''
      }
    },

    computed: {
      channelData() {
        return clone(this.channel)
      },

      modeLabel() {
        const mode = this.channel.mode
        return this.modes[mode]
      }

    },

    methods: {
      updateChannel() {
        if (!this.validateChannelName()) return

        const channelData = merge(this.channelSpecificData, {
          name: this.channelData.name,
          mode: this.channelData.mode
        })

        return channelService
          .patch(this.channel.id, channelData, [422])
          .then(response => {
            eventBus.$emit('update-channel', response.data)
            this.toggleEditMode()
          })
          .catch((error) => {
            if (error.response.status !== 422) return

            log.info('validation error when updating channel info', error)
            this.errors = error.response.data.errors
          })
          .then(this.clearUpdateChannelPromise)
      },

      validateChannelName() {
        const isChannelNameValid = !!this.channelData.name
        this.showChannelNameInvalidMessage = !isChannelNameValid
        return isChannelNameValid
      },

      clearUpdateChannelPromise() {
        this.promiseUpdateChannel = null
      },

      toggleEditMode() {
        this.editMode = !this.editMode
        this.channelData.name = this.channel.name
        this.errors = []
        this.showChannelNameInvalidMessage = false
      },

      showMode() {
        return ['mobile', 'rbm', 'viber_business_messages', 'whatsapp'].includes(this.channel.type)
      },

      getModeClass(approved) {
        const mode = this.channel.mode

        if (this.channel.type === 'viber_business_messages') {
          if (approved && mode === 'production') return 'text-success'
          if (approved) return 'text-warning'
          if (mode === 'production') return 'text-danger'
        }

        return ''
      },

      updateModeView(sendMessageLink, extraPopoverText = '', extraMode = {}) {
        const channelTypeFilter = Vue.filter('channelType')
        const channelType = channelTypeFilter(this.channel.type)

        const modeProduction = `<b>Production</b> - your <a href="${sendMessageLink}" target="_blank">Send Message</a>
           requests will be accepted and sent to ${channelType}.<br/><br/>`
        const modeTest = `<b>Test</b> - your <a href="${sendMessageLink}" target="_blank">Send Message</a>
           requests will be accepted but won't be processed any further. This avoids the cost of ${channelType} or connected integrations.`
        this.popoverTextMode = modeProduction + extraPopoverText + modeTest

        const approved = this.channel.viber_business_messages ? this.channel.viber_business_messages.approved : true
        this.modeClass = this.getModeClass(approved)

        this.modes = {
          test: 'Test',
          ...extraMode,
          production: 'Production'
        }
      },

      handleChannelAlert(show, type, text) {
        this.channelAlertShow = show
        this.channelAlertType = type
        this.channelAlertText = text
      }
    }
  }
</script>
